import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { areIntervalsOverlapping, differenceInCalendarDays, eachDayOfInterval, endOfDay, isToday } from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event } from './Event';
import { createTracks, denseArrangeTracks } from './util';

const useWeekViewClasses = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: 75,
        gridAutoRows: 'max-content',
        gridTemplateColumns: 'repeat(7, 1fr)',
        alignItems: 'start',
        backgroundImage: `
            linear-gradient(
                white,
                white 75px,
                transparent 75px,
                transparent
            ),
            repeating-linear-gradient(
                transparent,
                transparent 99px,
                ${theme.colors.neutral[9]} 99px,
                ${theme.colors.neutral[9]} 100px
            ),
            repeating-linear-gradient(
                90deg,
                transparent,
                transparent calc(100% / 7 - 1px),
                ${theme.colors.neutral[9]} calc(100% / 7 - 1px),
                ${theme.colors.neutral[9]} calc(100% / 7)
            );
            `,
        '&.drag-over > div:not(.dragging)': { pointerEvents: 'none' },
    },
    column: {
        alignSelf: 'stretch',
    },
    day: {
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '16px 0',
        borderBottom: `1px solid ${theme.colors.neutral[9]}`,
        color: theme.colors.neutral[10],
        backgroundColor: 'white',
        transition: `box-shadow ${theme.transitions.default}s`,
        '&.now': { color: theme.colors.darkBlue[5] },
        '&.drag-over': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
        '& > span:first-child': { ...theme.fonts.h4 },
        '& > span:last-child': { ...theme.fonts.h6, textTransform: 'uppercase' },
    },
    event: {
        overflow: 'hidden',
        margin: 8,
    },
});

/**
 * Calendar week view.
 *
 * @param props.context calendar context
 */
export const WeekView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useWeekViewClasses();

    const { t } = usePrefixTranslation('global', { global: 'calendar.WeekView' });
    const { events, intervals, onFilter, onDnD } = React.useContext(props.context);
    const weekEvents = React.useMemo(
        () => events.filter(event => areIntervalsOverlapping(event.interval, intervals.week)),
        [events, +intervals.week.start, +intervals.week.end],
    );
    const weekTracks = React.useMemo(() => {
        const tickResolution = eachDayOfInterval(intervals.week).map(day => ({ start: day, end: endOfDay(day) }));
        return denseArrangeTracks(createTracks(weekEvents, tickResolution));
    }, [weekEvents]);
    const [dragEvent, setDragEvent] = React.useState<CalendarEvent>();
    const [dragDay, setDragDay] = React.useState<number>();
    const renderLevel = Math.max(...weekTracks.tracks.map(track => track.level));

    React.useLayoutEffect(() => onFilter?.(weekEvents), [onFilter, weekEvents]);

    return (
        <div
            className={clsx(classes.root)}
            onDragStart={e => e.currentTarget.classList.add('drag-over')}
            onDrop={e => e.currentTarget.classList.remove('drag-over')}
            onDragEnd={e => e.currentTarget.classList.remove('drag-over')}
        >
            {eachDayOfInterval(intervals.week).map((day, i) => {
                const cellGridArea = `1/${1 + day.getDay()}/${renderLevel + 3}/auto`;
                const dayGridArea = `1/${1 + day.getDay()}/auto/auto`;
                const cls = { now: isToday(day) };
                return (
                    <React.Fragment key={i}>
                        <span
                            className={classes.column}
                            style={{ gridArea: cellGridArea }}
                            onDragEnter={() => dragEvent && setDragDay(i)}
                            onDragLeave={() => dragEvent && i === dragDay && setDragDay(undefined)}
                            onDragOver={e => dragEvent && e.preventDefault()}
                            onDrop={e => {
                                if (!dragEvent) return;
                                e.currentTarget.classList.remove('drag-over');
                                setDragEvent(undefined);
                                const difference = differenceInCalendarDays(day, dragEvent.interval.start);
                                if (difference === 0) return;
                                onDnD?.(dragEvent, { days: difference });
                            }}
                        />
                        <div
                            className={clsx(classes.day, cls, { 'drag-over': !!dragEvent && i === dragDay })}
                            style={{ gridArea: dayGridArea }}
                        >
                            <span>{day.getDate()}</span>
                            <span>{t('day', { date: day })}</span>
                        </div>
                    </React.Fragment>
                );
            })}
            {weekTracks.tracks.map((track, i) => {
                const gridArea = `${2 + track.level}/${1 + track.ticks.start}/auto/${2 + track.ticks.end}`;
                const cls = { dragging: track.events[0] === dragEvent };
                return (
                    <Event
                        key={i}
                        className={clsx(classes.event, cls)}
                        style={{ gridArea }}
                        event={track.events[0]}
                        view='week'
                        draggable={track.events[0].draggable && !!onDnD}
                        onDragStart={() => setDragEvent(track.events[0])}
                        onDragEnd={() => setDragEvent(undefined)}
                    />
                );
            })}
        </div>
    );
};
